import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  KernelLockup,
  Loading,
  SchemeToggle,
  resume,
  signIn,
  signOut,
} from "@lopie/kernel-ui";

import { api, onUnauthorizedCall, useTokenFrom } from "./lib/projects-api.ts";
import Projects from "./screens/Projects.jsx";
import ProjectView from "./screens/ProjectView.jsx";
import MyTasks from "./screens/MyTasks.jsx";
import GlobalSearch from "./components/GlobalSearch.jsx";

const DRAWER_WIDTH = 264;

/**
 * The project tracker.
 *
 * A left sidebar carries the navigation — Projects, My tasks, and the list of
 * projects — the way the tasks monolith it was ported from does, and the board
 * fills the rest of the width rather than sitting in a narrow centred column.
 * The sidebar is permanent on a wide screen and a drawer you open on a phone.
 * Assignees come from the kernel directory; nothing here keeps its own people.
 */
export default function App() {
  const [session, setSession] = useState(null);

  // Registered during render, not in an effect: a child screen's effect runs
  // before this component's would, so the first API call must already have a
  // token getter. A ref so the getter reads the latest session.
  const sessionRef = useRef(null);
  sessionRef.current = session;
  useTokenFrom(() => sessionRef.current?.token ?? null);
  onUnauthorizedCall(() => signIn());

  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("md"));

  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [deliberate, setDeliberate] = useState(false);
  const [openProject, setOpenProject] = useState(null);
  const [mine, setMine] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  // A search result names a task by its project id, not the (possibly
  // stale) project object the sidebar has cached, so a jump opens a minimal
  // stand-in; ProjectView re-fetches the real one through the board read.
  const [jumpTaskId, setJumpTaskId] = useState(null);
  // Who the caller is, as the *kernel* resolves them — the same Person id an
  // assignee and a follower carry. Not `claims.sub`, which is a Cognito uuid
  // that matches nothing the board stores.
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("signedout")) {
      setDeliberate(true);
      setReady(true);
      return;
    }
    resume()
      .then((found) => {
        setSession(found);
        if (!found) signIn();
      })
      .catch((e) => setError(e.message))
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!session) return;
    // Identity comes from /me/tasks, which already resolves the caller to their
    // kernel Person and returns it as `me`. A separate /me route would need an
    // API Gateway route a version bump cannot add to a live instance.
    api.myTasks().then((r) => setMe(r.me)).catch(() => setMe(null));
  }, [session]);

  // The sidebar's project list, kept current as you navigate — a project made
  // on the home screen shows up here the moment you come back to it.
  useEffect(() => {
    if (!session) return;
    api.projects().then((r) => setProjects(r.items)).catch(() => {});
  }, [session, openProject, mine]);

  const goHome = () => { setOpenProject(null); setMine(false); setNavOpen(false); };
  const goMine = () => { setOpenProject(null); setMine(true); setNavOpen(false); };
  const openOne = (p) => { setMine(false); setOpenProject(p); setNavOpen(false); };
  const openTaskFromSearch = (r) => {
    setMine(false);
    setOpenProject({ id: r.projectId, name: r.projectName, key: r.projectKey });
    setJumpTaskId(r.id);
    setNavOpen(false);
  };

  if (!ready) return <Loading label="Restoring your session" minHeight="100vh" />;

  if (!session) {
    return (
      <Box sx={{ p: 4, maxWidth: 560, mx: "auto" }}>
        <KernelLockup />
        <Alert severity={error ? "error" : "info"} variant="outlined" sx={{ mt: 3 }}>
          <AlertTitle>{error ? "Could not sign you in" : "Signed out"}</AlertTitle>
          {error ?? "Nothing here is readable without signing in."}
        </Alert>
        {deliberate ? (
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => signIn()}>
            Sign in
          </Button>
        ) : null}
      </Box>
    );
  }

  const nav = (
    <Stack sx={{ height: "100%" }}>
      <Box sx={{ px: 2, py: 2 }}>
        <KernelLockup />
      </Box>
      <Divider />
      <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
        <GlobalSearch onOpenProject={openOne} onOpenTask={openTaskFromSearch} />
      </Box>
      <List sx={{ flex: 1, overflowY: "auto", py: 0 }}>
        <ListItemButton selected={!openProject && !mine} onClick={goHome}>
          <ListItemText primary="Projects" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
        <ListItemButton selected={mine} onClick={goMine}>
          <ListItemText primary="My tasks" />
        </ListItemButton>

        <ListSubheader sx={{ bgcolor: "transparent", lineHeight: "2.2em", mt: 1 }}>
          PROJECTS
        </ListSubheader>
        {projects.map((p) => (
          <ListItemButton key={p.id} selected={openProject?.id === p.id}
            onClick={() => openOne(p)} sx={{ py: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "3px", mr: 1.5, flexShrink: 0,
                       bgcolor: p.color || "primary.main" }} />
            <ListItemText primary={p.name}
              primaryTypographyProps={{ noWrap: true, variant: "body2" }} />
            <Chip size="small" variant="outlined" label={p.key}
              sx={{ fontFamily: "monospace", height: 18, fontSize: 10 }} />
          </ListItemButton>
        ))}
        {!projects.length ? (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, display: "block", py: 1 }}>
            No projects yet.
          </Typography>
        ) : null}
      </List>
      <Divider />
      <Stack direction="row" spacing={1} sx={{ p: 1.5, alignItems: "center" }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {session.claims?.username ?? session.claims?.sub}
        </Typography>
        <SchemeToggle />
        <Button size="small" onClick={() => signOut()}>Sign out</Button>
      </Stack>
    </Stack>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {wide ? (
        <Drawer variant="permanent" open
          sx={{
            width: DRAWER_WIDTH, flexShrink: 0,
            "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
          }}>
          {nav}
        </Drawer>
      ) : (
        <Drawer variant="temporary" open={navOpen} onClose={() => setNavOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" } }}>
          {nav}
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {!wide ? (
          <AppBar position="static" color="transparent" elevation={0}
            sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Toolbar sx={{ gap: 1 }}>
              <IconButton edge="start" onClick={() => setNavOpen(true)} aria-label="Open navigation">
                <Box component="span" sx={{ fontSize: 22, lineHeight: 1 }}>&#9776;</Box>
              </IconButton>
              <Typography variant="h6">
                {openProject ? openProject.name : mine ? "My tasks" : "Projects"}
              </Typography>
            </Toolbar>
          </AppBar>
        ) : null}

        <Box sx={{ flex: 1, minWidth: 0, px: { xs: 2, sm: 3 }, pb: 4 }}>
          {openProject ? (
            <ProjectView project={openProject} me={me?.id} onBack={goHome}
              initialTaskId={jumpTaskId} onJumpConsumed={() => setJumpTaskId(null)} />
          ) : mine ? (
            <MyTasks onOpenProject={openOne} />
          ) : (
            <Projects onOpen={openOne} />
          )}
        </Box>
      </Box>
    </Box>
  );
}
